import { Theme, Themes } from './theme';
import { TButtonData, TCommonData, TCommonWidgetValue, TInputData } from './widget/data';
import { TCalculatorData } from './widget/dataCalculator';
import { ActionSystemActionType } from './widget/enums';
import { TCommonBottomSheetOption, TCommonInputOption, TWidgetCommonOption } from './widget/options';
import { TWAction, TWidget } from './widget/widget';

export enum EStatus {
    Idle = 'idle',
    Delay = 'delay',
    WidgetLoading = 'widgetLoading',
    Loading = 'loading',
    Succeeded = 'succeeded',
    Forbidden = 'forbidden',
    ShowChallenge = 'showChallenge',
    Failed = 'failed',
}

const SCENARIO = 'scenario/';

export enum ELocalStorageItems {
    THEME = 'theme_v1',
}

export enum StoreActionTypeEnum {
    updateWidgetValueByInternalId = SCENARIO + 'updateWidgetValueByInternalId',
    updateWidgetValueByKey = SCENARIO + 'updateWidgetValueByKey',
    updateWidgetValue = SCENARIO + 'updateWidgetValue',
}

export interface ValidationErrors {
    errorMessage: string;
    isForbidden?: boolean;
    captchaBody?: string;
    widgetUid?: string;
    actionForRepeat?: TWAction;
    field_errors?: Record<string, string>;
    /** The request failed because the device had no connection. */
    isOffline?: boolean;
}

export type TRequestUri = {
    path: string;
    data?: Record<string, string>;
};

export type TInitPayerOptions = {
    externalData?: Record<string, string>;
    requestUri?: TRequestUri;
    /** BCP 47 locale for `session.environment.locale`; embedded player keeps it for every request of that player. */
    locale?: string;
};

export type TPublicWebEmbeddedRenderOptions = {
    colors?: { background?: string; actionBackground?: string; text?: string; amountText?: string };
    skeleton?: string | Record<string, string>;
};

export type TEnterpriseEmbeddedRenderOptions = {
    /** Enterprise only: `true` turns on the new design, anything else keeps the old one. See embedded-player README. */
    rebranding?: boolean;
};

export type TEmbeddedRenderOptions = TPublicWebEmbeddedRenderOptions & TEnterpriseEmbeddedRenderOptions;

export type TEmbeddedPlayerInitOptions<TRenderOptions = TEmbeddedRenderOptions> = TInitPayerOptions & {
    deeplink?: string;
    renderOptions?: TRenderOptions;
};

export type TEmbeddedAppProps<TRenderOptions = TEmbeddedRenderOptions> = Pick<TInitPayerOptions, 'externalData'> & {
    renderOptions?: TRenderOptions;
};

export type IPayloadScenario = {
    scenarioId: string;
    currentPageId: string;
    configuration?: TConfiguration;
} & TInitWidgetState;

/** How to send a request. Passed unchanged through triggerAction → thunk → fetchJson → fetchWithDelay. */
export type TRequestOptions = {
    isShowLoader?: boolean;
    widgetUid?: string;
    keepalive?: boolean;
    playerId?: string;
    initPayerOptions?: TInitPayerOptions;
    skipDelay?: boolean;
};

export type TTriggerWidgetAction = TRequestOptions & {
    action: TWAction | undefined;
    scenarioId?: IPayloadScenario['scenarioId'];
    currentPageId?: IPayloadScenario['currentPageId'];
};

export interface IBaseState {
    status?: EStatus;
    errors?: ValidationErrors;
}
/** Feature flags sent by the backend in `configuration.featureFlags`. */
// @deprecated
export enum EFeatureFlag {
    /** Render widgets in the new (rebranded) design. */
    Rebranding = 'rebranding',
}
// @deprecated
export type TFeatureFlags = Partial<Record<EFeatureFlag, boolean>>;
/** Design system the player renders app-level UI in, sent by the backend in `configuration.designSystem`. */
export type TDesignSystem = {
    /**
     * Design generation (1 — original, 2 — new) for UI that has no owning widget: loader, error screen, ….
     * Widgets don't inherit it — each one renders by its own `TWidget.version`.
     */
    generation?: number;
};

export type TConfiguration = {
    screenshotAvailable?: boolean;
    themes?: Themes;
    /**
     * Optional; sent only in the `requestInteraction` response. Persisted in localStorage, so a response without it
     * keeps the last received value — and app-level screens know it before the first response after a reload.
     */
    designSystem?: TDesignSystem;
    /**
     * Sent only in the `requestInteraction` response; absent in every later one. Persisted in localStorage and merged
     * per flag, so a response without it (or without one flag) keeps the last received values, also after a reload.
     */
    // @deprecated
    featureFlags?: TFeatureFlags;
};

export type IMutableWidgetsMap<TD, TO> = Record<string, TWidget<TD, TO>>;
export type TModalWindow = {
    uid: Array<string>;
    isOpen: boolean;
    options: TCommonBottomSheetOption;
};
export type TBottomSheetsWidgetsMap = Record<string, TModalWindow>;
export type TModalWidgetsMap = Record<string, TModalWindow>;
export type TSystemActin = {
    action: TWAction;
    widget: TWidget<TCommonData>;
};
export type TSystemActions = Record<ActionSystemActionType, TSystemActin>;
export type TMutableWidgetsData = TInputData | TCalculatorData | TButtonData | TCommonData;
export type TMutableWidgetsOption = TWidgetCommonOption & TCommonInputOption;
export type TInternalWidgets = Record<string, string>;
export type TInitWidgetState = {
    mutableWidgetsMap: IMutableWidgetsMap<TMutableWidgetsData, TMutableWidgetsOption>;
    bottomSheetMap: TBottomSheetsWidgetsMap;
    modalWindowMap: TModalWidgetsMap;
    systemActions: TSystemActions;
    internalIdWidgets: TInternalWidgets;
    functions?: Record<string, TWidget<TCommonData>['functions']>;
    submitWidget?: TWidget<TButtonData>;
    rootWidgets?: Array<string>;
};

export type TStateScenario = {
    scenarioId: string;
    currentPageId: string;
    playerId?: string;
    theme: Theme;
    configuration?: TConfiguration;
    /**
     * `requestId` of the thunk that put the player into its current pending state.
     *
     * Only that thunk may take the player out of it again: an aborted request is rejected without a
     * payload, and without this guard clearing the status would also clear the loader of a newer
     * request that is still running.
     */
    pendingRequestId?: string;
} & IBaseState &
    TInitWidgetState;

export interface IStateSession extends IBaseState {
    auth?: Record<string, string>;
    environment: IEnvironment;
    parent?: IStateSession;
}

export interface IEnvironment {
    realm: string;
    locale: string;
    data: IEnvironmentData;
    requestUri?: TRequestUri;
}

/** Form factor of the device the player runs on. Values mirror the UA Client Hints `formFactors` taxonomy. */
export enum EDeviceType {
    PC = 'PC',
    Mobile = 'Mobile',
    Tablet = 'Tablet',
    Watch = 'Watch',
    TV = 'TV',
    Other = 'Other',
}

export interface IEnvironmentData {
    deviceTheme: string;
    userAgent: string;
    operatingSystem: string;
    deviceType: EDeviceType;
    model?: string;
    id: string;
    deviceId: string;
    authApp: string;
    authSource: string;
    screenHeight: string;
    screenWidth: string;
    browserTimeZone: string;
    browserTimeZoneOffset: string;
    requestTimestamp: string;
    colorDepth: string;
    screenPrint: string;
    externalData?: Record<string, string>;
    extensions?: Record<string, string | object>;
}

export interface IBody {
    session: IStateSession;
    action?: TWAction;
}

export interface IRootState {
    scenario: TStateScenario;
}

export interface IUpdateWidgetPropertyByPath {
    widget?: TWidget<TCommonData>;
    uid?: string;
    path: string;
    value: TCommonWidgetValue;
}
